import { plain } from '../../lib/labels';
import { useEffect, useState } from 'react';
import {
  Badge,
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  Select,
  InputNumber,
  Drawer,
  Descriptions,
  Empty,
  App,
  Row,
  Col,
  Statistic,
  Tooltip,
  Tabs,
  Grid,
  type TableColumnsType,
} from 'antd';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useUrlState, oneOf, intParam, pageSizeParam } from '../../lib/urlState';
import { QueryError } from '../../lib/QueryState';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';
import { formatRupees } from '../../lib/money';
import { ListenerName, listenerText } from '../../lib/listenerName';
import { meetingLinkError } from './meetingLink';
import {
  formatDuration,
  formatWhen,
  type ListenerRowStats,
  type ListenerSummary,
  type Paged,
} from './listenerStats';
import {
  LiveDuration,
  LoginStatusTag,
  OnlineStatusTag,
  StatsRangeFilter,
  useNow,
  useUrlStatsRange,
} from './ListenerStatsWidgets';
import { OnlineExportButton } from './OnlineExportButton';
import type { BadgeMap } from './employment/employmentTypes';
import { MasterDetail } from '../../lib/MasterDetail';
import { ListenerDetailView } from './ListenerDetailPage';

type Tier = 'peer' | 'coach' | 'clinical';
type Status = 'pending' | 'interview_scheduled' | 'approved' | 'rejected';
/** "All listeners" = every APPROVED listener — kept in the URL as `?status=all`. */
const ALL = 'all';
/** Tab key for the Dummy Listener placeholders (any status) — `?status=dummy`. */
const DUMMY = 'dummy';
/** Ranked / live views of APPROVED listeners (the API's `view` param). */
type View = 'top_video' | 'top_audio' | 'live' | 'online' | 'busy';
const VIEWS: readonly View[] = ['top_video', 'top_audio', 'live', 'online', 'busy'];
const isView = (t: Tab): t is View => (VIEWS as readonly string[]).includes(t);
type Tab = typeof ALL | View | 'pending' | 'interview_scheduled' | 'rejected' | typeof DUMMY;
type Sort = 'newest' | 'oldest' | 'name';
/**
 * Approval status decides the tab: approved listeners live under All and the
 * views; a pending application appears ONLY under Pending and a rejected one
 * ONLY under Rejected — neither is ever in All.
 */
const STATUS_TABS: Array<{ key: Tab; label: string; empty: string }> = [
  { key: ALL, label: 'All listeners', empty: 'No approved listeners' },
  { key: 'top_video', label: 'Top video', empty: 'No connected video calls in this period' },
  { key: 'top_audio', label: 'Top audio', empty: 'No connected audio calls in this period' },
  { key: 'live', label: 'Live now', empty: 'No listener is hosting a live stream right now' },
  { key: 'online', label: 'Online now', empty: 'No listener is online right now' },
  { key: 'busy', label: 'Busy now', empty: 'No listener is in a call right now' },
  { key: 'pending', label: 'Pending', empty: 'No applications waiting for a decision' },
  { key: 'interview_scheduled', label: 'Interview scheduled', empty: 'No interviews scheduled' },
  { key: 'rejected', label: 'Rejected', empty: 'No rejected applications' },
  { key: DUMMY, label: 'Placeholder profiles', empty: 'No placeholder profiles' },
];
/** What the list / summary / export endpoints take for each tab. */
/** `GET /admin/listeners/tab-counts` — how many listeners each tab holds. */
interface TabCounts {
  approved: number;
  pending: number;
  interview_scheduled: number;
  rejected: number;
  dummy: number;
  online: number;
  live: number;
  busy: number;
}
/** The count shown on a tab. The two rankings have none: their size depends on the date range. */
function tabCount(tab: Tab, c: TabCounts | undefined): number | undefined {
  if (!c) return undefined;
  if (tab === ALL) return c.approved;
  if (tab === DUMMY) return c.dummy;
  if (tab === 'top_video' || tab === 'top_audio') return undefined;
  return c[tab];
}

function tabFilter(tab: Tab): Record<string, string> {
  if (tab === ALL) return { status: 'approved' };
  if (tab === DUMMY) return { dummy: 'true' };
  if (isView(tab)) return { view: tab };
  return { status: tab };
}
type KycType = 'pan' | 'aadhaar' | 'licence' | 'other';
type KycStatus = 'uploaded' | 'verified' | 'rejected';

interface AdminListener {
  id: string;
  userId: string;
  displayName: string;
  bio: string;
  tier: Tier;
  status: Status;
  isSuspended: boolean;
  /** Admin-flagged placeholder: shown online, never rung, calls fall through to a real listener. */
  isDummy?: boolean;
  dummySetAt?: string | null;
  /** Admin-marked HOT (the lowest HOT priority: live and reliable buddies fill the 5 slots first). */
  isHot?: boolean;
  hotSetAt?: string | null;
  verifiedProfessional: boolean;
  perMinuteRatePaise: string;
  languages: string[];
  expertiseTags: string[];
  rejectionReason: string | null;
  /** When a rejected application was rejected (absent on older API builds). */
  rejectedAt?: string | null;
  /** Call-mode switches the listener controls from the app (absent on older API builds). */
  acceptsAudio?: boolean;
  acceptsVideo?: boolean;
  acceptsChat?: boolean;
  voiceVerifiedAt?: string | null;
  /** Set on a ranked / live view: position and the figure it is ranked by. */
  view?: null | { rank: number; modeSeconds?: number; liveNow?: 'busy' | 'live' };
  user: {
    handle: string;
    phoneLast4: string;
    /** Full number — only sent to admins holding `users.reveal_phone`, else null. */
    phone: string | null;
  };
  kycDocuments: Array<{
    id: string;
    type: KycType;
    status: KycStatus;
    last4: string | null;
    rejectionReason: string | null;
    createdAt: string;
  }>;
  createdAt: string;
  /** Login / online / calls / earnings for the selected range (API-computed). */
  stats: ListenerRowStats;
}

const tierColor: Record<Tier, string> = { peer: 'blue', coach: 'magenta', clinical: 'gold' };
// Single tier now; `peer` displays as "Listener". Coach/Clinical kept only for
// any legacy rows still carrying those values.
const tierLabel: Record<Tier, string> = { peer: 'Listener', coach: 'Coach', clinical: 'Clinical' };

function SummaryCard({
  title,
  value,
  loading,
  hint,
}: {
  title: string;
  value: string | number | undefined;
  loading: boolean;
  hint?: string;
}) {
  return (
    <Col xs={12} sm={8} md={6} xl={3}>
      <Card size="small" loading={loading}>
        <Statistic title={hint ? <Tooltip title={hint}>{title}</Tooltip> : title} value={value ?? '—'} />
      </Card>
    </Col>
  );
}

/** Full number (copyable) when the API sent it; masked last-4 otherwise. */
function ListenerPhone({ user }: { user: AdminListener['user'] }) {
  if (user.phone) {
    return <Typography.Text copyable={{ text: user.phone }}>{user.phone}</Typography.Text>;
  }
  return <>{user.phoneLast4 ? `••• ${user.phoneLast4}` : '—'}</>;
}

export function ListenersPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  // Tab, search, sort, page and page size all live in the URL (lib/urlState), so
  // Back from Listener Details lands on exactly the list the admin left.
  // `?status=approved` (old links) is the All tab: All IS the approved list.
  const { get, set } = useUrlState();
  const statusParam = get('status');
  const status: Tab =
    statusParam === 'approved' ? ALL : STATUS_TABS.some((t) => t.key === statusParam) ? (statusParam as Tab) : ALL;
  const sort = oneOf(get('sort'), ['newest', 'oldest', 'name'] as const, 'newest') as Sort;
  // A view fixes its own order (its ranking); the plain tabs take `sort`.
  const tabParams = isView(status) ? tabFilter(status) : { ...tabFilter(status), sort };
  const tab = STATUS_TABS.find((t) => t.key === status) ?? STATUS_TABS[0]!;
  const setStatus = (t: Tab) => set({ status: t, page: null });
  const setSort = (v: Sort) => set({ sort: v === 'newest' ? null : v, page: null });
  const [reviewing, setReviewing] = useState<AdminListener | null>(null);
  // A row's quick action (Approve / Reject / Suspend) opens ONLY that action's
  // confirmation dialog — not the review drawer behind it.
  const [quick, setQuick] = useState(false);
  const [approveModal, setApproveModal] = useState<{ tier: Tier; rateVideoRupees: number } | null>(null);
  const [rateModal, setRateModal] = useState<{ id: string; rateVideoRupees: number } | null>(null);
  // Separate "modal open" boolean from the textarea value. The earlier
  // code used `rejectReason === ''` as the open/closed sentinel, which
  // forced an awkward `' '`-space hack to keep the modal open while the
  // textarea was empty.
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [suspendModal, setSuspendModal] = useState<{ reason: string; days: number } | null>(null);
  const [dummyModal, setDummyModal] = useState<{ enabled: boolean; reason: string } | null>(null);
  const [scheduleModal, setScheduleModal] = useState<{ scheduledAt: string; durationMin: number; meetingLink: string } | null>(null);

  // Master–detail: `?listener=` is the listener whose details are open in the
  // right-hand pane (pushed, so Back closes it); `?tab=` is its open tab. On a
  // wide screen the table keeps only its identity columns while the pane is
  // open; below `lg` the details are a full-width drawer and the table is whole.
  const selectedId = get('listener');
  const openListener = (id: string, tab?: string) =>
    set(tab ? { listener: id, tab } : { listener: id }, { push: true });
  const closeListener = () => set({ listener: null, tab: null });
  const inline = Grid.useBreakpoint().lg === true;
  const compact = inline && !!selectedId;
  const { can } = usePermissions();
  const canActivity = can('listeners.activity.view');
  const canEarnings = can('listeners.earnings.view');
  const { preset, setPreset, custom, setCustom, range } = useUrlStatsRange('today');
  const q = get('q') ?? '';
  const setQ = (v: string) => set({ q: v || null, page: null });
  const page = intParam(get('page'), 1);
  // 10 by default so a single status tab (e.g. 23 approved) actually pages —
  // at 25 the pager had only one page and looked missing.
  const pageSize = pageSizeParam(get('pageSize'), [10, 25, 50, 100]);
  const now = useNow();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'listeners', status, sort, q, page, pageSize, range],
    queryFn: async () =>
      (
        await api.get<Paged<AdminListener>>('/admin/listeners', {
          params: { ...tabParams, q: q || undefined, page, pageSize, ...range },
        })
      ).data,
    enabled: range !== null,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  // Employee tag per row — one cheap call per page, only with the permission.
  const pageIds = (data?.items ?? []).map((l) => l.id);
  const canEmployment = can('listeners.employment.view');
  const employmentBadges = useQuery({
    queryKey: ['admin', 'employment', 'badges', pageIds.join(',')],
    queryFn: async () =>
      (await api.get<BadgeMap>('/admin/employment/badges', { params: { listenerIds: pageIds.join(',') } })).data,
    enabled: canEmployment && pageIds.length > 0,
    staleTime: 60_000,
  });
  const badgeOf = (listenerId: string) => {
    const b = employmentBadges.data?.[listenerId];
    return b && typeof b === 'object' && 'status' in b && b.status !== 'ended' ? b : null;
  };

  const summary = useQuery({
    queryKey: ['admin', 'listeners', 'summary', status, q, range],
    queryFn: async () =>
      (
        await api.get<ListenerSummary>('/admin/listeners/summary', {
          params: { ...tabFilter(status), q: q || undefined, ...range },
        })
      ).data,
    enabled: range !== null,
    refetchInterval: 60_000,
  });

  const approve = useMutation({
    mutationFn: ({ id, tier, perMinuteRatePaise }: { id: string; tier: Tier; perMinuteRatePaise: string }) =>
      api.post(`/admin/listeners/${id}/approve`, { tier, perMinuteRatePaise }),
    onSuccess: () => {
      message.success('Listener approved');
      qc.invalidateQueries({ queryKey: ['admin', 'listeners'] });
      qc.invalidateQueries({ queryKey: ['admin', 'console', 'attention'] });
      setReviewing(null);
      setApproveModal(null);
    },
    onError: () => message.error('Could not approve'),
  });

  const setRate = useMutation({
    mutationFn: ({ id, perMinuteRatePaise }: { id: string; perMinuteRatePaise: string }) =>
      api.patch(`/admin/listeners/${id}/rate`, { perMinuteRatePaise }),
    onSuccess: () => {
      message.success('Per-minute rate updated');
      qc.invalidateQueries({ queryKey: ['admin', 'listeners'] });
      setReviewing(null);
      setRateModal(null);
    },
    onError: () => message.error('Could not update rate'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/admin/listeners/${id}/reject`, { reason }),
    onSuccess: () => {
      message.success('Listener rejected');
      qc.invalidateQueries({ queryKey: ['admin', 'listeners'] });
      qc.invalidateQueries({ queryKey: ['admin', 'console', 'attention'] });
      setReviewing(null);
      setRejectOpen(false);
      setRejectReason('');
    },
    onError: () => message.error('Could not reject'),
  });

  const scheduleInterview = useMutation({
    mutationFn: ({
      id,
      scheduledAt,
      durationMin,
      meetingLink,
    }: {
      id: string;
      scheduledAt: string;
      durationMin: number;
      meetingLink?: string;
    }) =>
      api.post(`/admin/listeners/${id}/schedule-interview`, {
        scheduledAt,
        durationMin,
        // Only send a link when one was entered (backend validates URL).
        meetingLink: meetingLink && meetingLink.trim() ? meetingLink.trim() : undefined,
      }),
    onSuccess: () => {
      message.success('Interview scheduled. Listener will be notified.');
      qc.invalidateQueries({ queryKey: ['admin', 'listeners'] });
      setScheduleModal(null);
      setReviewing(null);
    },
    // Show the server's reason (e.g. an invalid meeting link), not a bare failure.
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      message.error(typeof msg === 'string' && msg ? msg : 'Could not schedule interview');
    },
  });

  const suspend = useMutation({
    mutationFn: ({ id, reason, durationDays }: { id: string; reason: string; durationDays: number }) =>
      api.post(`/admin/listeners/${id}/suspend`, { reason, durationDays }),
    onSuccess: () => {
      message.success('Listener suspended');
      qc.invalidateQueries({ queryKey: ['admin', 'listeners'] });
      setSuspendModal(null);
      setReviewing(null);
    },
    onError: () => message.error('Could not suspend'),
  });

  // Dummy listener: a placeholder profile users see online that never rings;
  // a call to it moves to a real listener after the dummy attempt timeout.
  const setDummy = useMutation({
    mutationFn: ({ id, enabled, reason }: { id: string; enabled: boolean; reason: string }) =>
      api.post(`/admin/listeners/${id}/dummy`, { enabled, reason }),
    onSuccess: (_res, vars) => {
      message.success(vars.enabled ? 'Marked as dummy listener' : 'Dummy flag removed');
      qc.invalidateQueries({ queryKey: ['admin', 'listeners'] });
      setDummyModal(null);
      setReviewing(null);
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      message.error(typeof msg === 'string' && msg ? msg : 'Could not update dummy flag');
    },
  });

  // HOT buddy: an admin mark is the LOWEST HOT priority — live buddies, then
  // yesterday's reliable answerers, fill the 5 HOT slots first.
  const setHot = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.post(`/admin/listeners/${id}/hot`, { enabled }),
    onSuccess: (_res, vars) => {
      message.success(vars.enabled ? 'Marked as hot' : 'Hot mark removed');
      qc.invalidateQueries({ queryKey: ['admin', 'listeners'] });
      setReviewing(null);
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      message.error(typeof msg === 'string' && msg ? msg : 'Could not update hot mark');
    },
  });

  // Consultation pivot: set/clear the verified-professional badge after the
  // counselor's credential documents (KYC) have been reviewed.
  const verifyProfessional = useMutation({
    mutationFn: ({ id, verified }: { id: string; verified: boolean }) =>
      api.post(`/admin/listeners/${id}/verify-professional`, { verified }),
    onSuccess: (_res, vars) => {
      message.success(vars.verified ? 'Marked as verified counselor' : 'Verification removed');
      qc.invalidateQueries({ queryKey: ['admin', 'listeners'] });
      setReviewing(null);
    },
    onError: () => message.error('Could not update verification'),
  });

  const tabCounts = useQuery({
    queryKey: ['admin', 'listeners', 'tab-counts'],
    queryFn: async () => (await api.get<TabCounts>('/admin/listeners/tab-counts')).data,
    refetchInterval: 30_000,
  });

  // Quick action finished or cancelled: nothing is being reviewed any more.
  useEffect(() => {
    if (quick && !approveModal && !rejectOpen && !suspendModal) {
      setReviewing(null);
      setQuick(false);
    }
  }, [quick, approveModal, rejectOpen, suspendModal]);

  const review = (row: AdminListener) => {
    setQuick(false);
    setReviewing(row);
  };
  const quickApprove = (row: AdminListener) => {
    setQuick(true);
    setReviewing(row);
    // stored paise is the caller CHARGE; show the listener's video rate (÷100 → ₹).
    setApproveModal({ tier: row.tier, rateVideoRupees: Number(row.perMinuteRatePaise) / 100 });
  };
  const quickReject = (row: AdminListener) => {
    setQuick(true);
    setReviewing(row);
    setRejectReason('');
    setRejectOpen(true);
  };
  const quickSuspend = (row: AdminListener) => {
    setQuick(true);
    setReviewing(row);
    setSuspendModal({ reason: '', days: 7 });
  };

  const when = (iso?: string | null) => (iso ? formatWhen(iso) : '—');
  const allColumns: TableColumnsType<AdminListener> = [
    ...(isView(status)
      ? ([
          { title: '#', fixed: 'left', width: 50, render: (_: unknown, row) => row.view?.rank ?? '—' },
        ] as TableColumnsType<AdminListener>)
      : []),
    {
      key: 'listener',
      title: 'Listener',
      fixed: 'left',
      width: 190,
      render: (_: unknown, row) => (
        <Space direction="vertical" size={2}>
          <ListenerName
            listener={{ displayName: row.displayName, handle: row.user.handle, id: row.id }}
            listenerId={row.id}
            onOpen={(id) => openListener(id)}
          />
          {badgeOf(row.id) && (
            <Tag color="geekblue" style={{ fontSize: 11, lineHeight: '18px' }}>
              Employee · {plain(badgeOf(row.id)!.employmentType)}
            </Tag>
          )}
          <Typography.Text copyable={{ text: row.userId }} style={{ fontSize: 11, color: '#8B8B8B' }}>
            {row.userId.slice(0, 8)}…
          </Typography.Text>
        </Space>
      ),
    },
    {
      key: 'phone',
      title: 'Phone',
      width: 150,
      render: (_: unknown, row) => <ListenerPhone user={row.user} />,
    },
    ...(status === 'top_video' || status === 'top_audio'
      ? ([
          {
            title: (
              <Tooltip title="Connected talk time of this call type, for calls started in the selected range">
                {status === 'top_video' ? 'Video call time' : 'Audio call time'}
              </Tooltip>
            ),
            width: 130,
            render: (_: unknown, row) => <strong>{formatDuration(row.view?.modeSeconds)}</strong>,
          },
        ] as TableColumnsType<AdminListener>)
      : []),
    ...(status === 'online' || status === 'live' || status === 'busy'
      ? ([
          {
            title: <Tooltip title="Which call modes the listener has switched on in the app">Takes</Tooltip>,
            width: 130,
            render: (_: unknown, row) =>
              row.acceptsAudio === undefined && row.acceptsVideo === undefined ? (
                '—'
              ) : (
                <Space size={4}>
                  <Tag color={row.acceptsAudio ? 'green' : 'default'}>Audio {row.acceptsAudio ? 'on' : 'off'}</Tag>
                  <Tag color={row.acceptsVideo ? 'green' : 'default'}>Video {row.acceptsVideo ? 'on' : 'off'}</Tag>
                </Space>
              ),
          },
        ] as TableColumnsType<AdminListener>)
      : []),
    ...(status === 'live' || status === 'busy'
      ? ([
          {
            title: 'Right now',
            width: 110,
            render: (_: unknown, row) =>
              row.view?.liveNow === 'busy' ? <Tag color="orange">In a call</Tag> : <Tag color="red">Live stream</Tag>,
          },
        ] as TableColumnsType<AdminListener>)
      : []),
    ...(status === 'pending' || status === 'interview_scheduled' || status === 'rejected'
      ? ([
          { title: 'Applied on', width: 140, render: (_: unknown, row) => when(row.createdAt) },
        ] as TableColumnsType<AdminListener>)
      : []),
    ...(status === 'rejected'
      ? ([
          { title: 'Rejected on', width: 140, render: (_: unknown, row) => when(row.rejectedAt) },
          {
            title: 'Rejection reason',
            width: 240,
            render: (_: unknown, row) => (
              <Typography.Text style={{ maxWidth: 240, display: 'inline-block', whiteSpace: 'pre-wrap' }}>
                {row.rejectionReason || '—'}
              </Typography.Text>
            ),
          },
        ] as TableColumnsType<AdminListener>)
      : []),
    ...(canActivity
      ? ([
          {
            title: 'Login status',
            width: 110,
            render: (_: unknown, row) => <LoginStatusTag loggedIn={row.stats.login?.loggedIn} />,
          },
          {
            title: 'Last login',
            width: 130,
            render: (_: unknown, row) => formatWhen(row.stats.login?.lastLoginAt),
          },
          {
            title: <Tooltip title="Authenticated app time in the selected range (overlapping devices counted once)">Login time</Tooltip>,
            width: 100,
            render: (_: unknown, row) => formatDuration(row.stats.login?.loginSeconds),
          },
          {
            title: 'Online status',
            width: 150,
            render: (_: unknown, row) => (
              <OnlineStatusTag
                online={row.stats.online?.onlineNow}
                screenOn={row.stats.online?.screenOn}
                reachableUntil={row.stats.online?.reachableUntil}
              />
            ),
          },
          {
            title: 'Current online session',
            width: 120,
            render: (_: unknown, row) => (
              <LiveDuration since={row.stats.online?.currentOnlineSince} now={now} />
            ),
          },
          {
            title: <Tooltip title="Time available to receive calls in the selected range">Online time</Tooltip>,
            width: 100,
            render: (_: unknown, row) => formatDuration(row.stats.online?.onlineSeconds),
          },
        ] as TableColumnsType<AdminListener>)
      : []),
    {
      title: <Tooltip title="Connected talk time of calls started in the selected range">Call time</Tooltip>,
      width: 100,
      render: (_: unknown, row) => formatDuration(row.stats.calls.callSeconds),
    },
    {
      title: <Tooltip title="Connected calls / all call attempts">Calls</Tooltip>,
      width: 80,
      render: (_: unknown, row) => `${row.stats.calls.connectedCalls} / ${row.stats.calls.calls}`,
    },
    ...(canEarnings
      ? ([
          {
            title: <Tooltip title="₹ credited to the listener's earnings wallet (calls, gifts, referral)">Earnings</Tooltip>,
            width: 100,
            render: (_: unknown, row) => formatRupees(row.stats.money?.earningsPaise),
          },
        ] as TableColumnsType<AdminListener>)
      : []),
    ...(canActivity
      ? ([
          {
            title: 'Last online',
            width: 130,
            render: (_: unknown, row) =>
              row.stats.online?.onlineNow ? <Tag color="success">Now</Tag> : formatWhen(row.stats.online?.lastOnlineAt),
          },
        ] as TableColumnsType<AdminListener>)
      : []),
    {
      key: 'application',
      title: 'Application',
      dataIndex: 'status',
      width: 150,
      render: (s: Status, row) => (
        <Space size={4} wrap>
          <Tag color={s === 'approved' ? 'success' : s === 'rejected' ? 'error' : 'processing'}>{plain(s)}</Tag>
          {row.isSuspended && <Tag color="error">suspended</Tag>}
          {row.isDummy && <Tag color="purple">dummy</Tag>}
          {row.isHot && <Tag color="volcano">hot</Tag>}
        </Space>
      ),
    },
    {
      title: 'ID documents',
      width: 150,
      render: (_: unknown, row) => {
        const counts = row.kycDocuments.reduce<Record<KycStatus, number>>(
          (acc, d) => ({ ...acc, [d.status]: (acc[d.status] ?? 0) + 1 }),
          { uploaded: 0, verified: 0, rejected: 0 },
        );
        return (
          <Space size={4}>
            <Tag color="green">{counts.verified}✓</Tag>
            <Tag>{counts.uploaded}…</Tag>
            <Tag color="red">{counts.rejected}✗</Tag>
          </Space>
        );
      },
    },
    {
      title: 'Submitted',
      dataIndex: 'createdAt',
      width: 100,
      render: (s: string) => new Date(s).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      ...(compact ? { width: 190 } : { fixed: 'right' as const, width: 300 }),
      render: (_: unknown, row) => {
        const undecided = row.status === 'pending' || row.status === 'interview_scheduled';
        return (
          <Space size={4} wrap>
            {undecided && (
              <>
                <Can permission="listeners.approve">
                  <Button size="small" type="primary" onClick={() => quickApprove(row)}>
                    Approve
                  </Button>
                </Can>
                <Can permission="listeners.reject">
                  <Button size="small" danger onClick={() => quickReject(row)}>
                    Reject
                  </Button>
                </Can>
              </>
            )}
            <Button size="small" type={undecided ? 'default' : 'primary'} onClick={() => review(row)}>
              Review
            </Button>
            {row.status === 'approved' && !compact && (
              <>
                <Button size="small" onClick={() => openListener(row.id, 'calls')}>
                  Calls
                </Button>
                {canEarnings && (
                  <Button size="small" onClick={() => openListener(row.id, 'earnings')}>
                    Earnings
                  </Button>
                )}
                {!row.isSuspended && (
                  <Can permission="listeners.suspend">
                    <Button size="small" danger type="text" onClick={() => quickSuspend(row)}>
                      Suspend
                    </Button>
                  </Can>
                )}
              </>
            )}
            {!compact && (
              <Button size="small" type="link" onClick={() => openListener(row.id)}>
                Details
              </Button>
            )}
          </Space>
        );
      },
    },
  ];
  // While the pane is open the table keeps who / status / actions; the rest is
  // in the pane itself.
  const columns: TableColumnsType<AdminListener> = compact
    ? allColumns.filter((c) => ['listener', 'application', 'actions'].includes(String(c.key)))
    : allColumns;
  // The selected listener's row, when it is on this page — gives the pane its
  // Review button (approve / reject / rate / suspend / dummy live in that drawer).
  const selectedRow = selectedId ? (data?.items ?? []).find((l) => l.id === selectedId) : undefined;

  const sum = summary.data;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          Listeners
        </Typography.Title>
        <Space wrap>
        <Input.Search
          allowClear
          aria-label="Search listeners"
          placeholder="Name, @handle, phone last-4 or id"
          key={q}
          defaultValue={q}
          onSearch={(v) => setQ(v.trim())}
          style={{ width: 260 }}
        />
        {!isView(status) && (
          <Select<Sort>
            aria-label="Sort"
            value={sort}
            onChange={setSort}
            style={{ width: 150 }}
            options={[
              { value: 'newest', label: 'Newest first' },
              { value: 'oldest', label: 'Oldest first' },
              { value: 'name', label: 'Name A–Z' },
            ]}
          />
        )}
        <StatsRangeFilter preset={preset} setPreset={setPreset} setCustom={setCustom} custom={custom} />
        {/* The export is a filter of listeners, not a ranking: not on the views. */}
        {canActivity && !isView(status) && (
          <OnlineExportButton
            filter={{ ...tabFilter(status), q: q || undefined }}
            scopeLabel={tab.label}
            searched={q !== ''}
          />
        )}
        </Space>
      </div>

      <Tabs
        activeKey={status}
        onChange={(k) => setStatus(k as Tab)}
        items={STATUS_TABS.map((t) => {
          const n = tabCount(t.key, tabCounts.data);
          return {
            key: t.key,
            label:
              n === undefined ? (
                t.label
              ) : (
                <span>
                  {t.label}{' '}
                  <Badge
                    count={n}
                    showZero
                    overflowCount={9999}
                    size="small"
                    color={t.key === 'pending' && n > 0 ? '#EF4444' : 'rgba(255,255,255,0.18)'}
                    title={`${n} in ${t.label}`}
                  />
                </span>
              ),
          };
        })}
        style={{ marginBottom: -16 }}
      />

      {/* Summary cards — every figure is for the selected range except the
          live online/offline counts. Login and Online time are separate. */}
      <Row gutter={[12, 12]}>
        <SummaryCard title="Total listeners" value={sum?.totalListeners} loading={summary.isLoading} />
        <SummaryCard
          title="Currently online"
          value={sum?.onlineNow}
          loading={summary.isLoading}
          hint="Approved, not suspended, and available to receive calls right now"
        />
        <SummaryCard title="Currently offline" value={sum?.offlineNow} loading={summary.isLoading} />
        {canActivity && (
          <>
            <SummaryCard title="Login time" value={formatDuration(sum?.loginSeconds)} loading={summary.isLoading} hint="Authenticated app time, summed over listeners" />
            <SummaryCard title="Online time" value={formatDuration(sum?.onlineSeconds)} loading={summary.isLoading} hint="Time available to receive calls, summed over listeners" />
          </>
        )}
        <SummaryCard
          title="Call time"
          value={formatDuration(sum?.callSeconds)}
          loading={summary.isLoading}
          hint={sum ? `${sum.connectedCalls} connected of ${sum.calls} calls` : undefined}
        />
      </Row>

      <MasterDetail
        open={!!selectedId}
        onClose={closeListener}
        chrome={false}
        drawerWidth={960}
        list={
      <Card>
        {isError && !data && (
          <QueryError error={error} what="listeners" onRetry={() => void refetch()} style={{ marginBottom: 12 }} />
        )}
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={data?.items ?? []}
          columns={columns}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: <Empty description={tab.empty} />,
          }}
          // The whole row opens the listener; links, buttons and copy icons
          // inside it keep their own meaning.
          onRow={(row) => ({
            onClick: (e) => {
              if ((e.target as HTMLElement).closest('a, button, .ant-typography-copy')) return;
              if (row.id !== selectedId) openListener(row.id);
            },
          })}
          rowClassName={(row) =>
            row.id === selectedId ? 'master-detail-row master-detail-row-selected' : 'master-detail-row'
          }
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
            showQuickJumper: (data?.total ?? 0) > pageSize * 5,
            showTotal: (t) => `${t.toLocaleString('en-IN')} listeners`,
            onChange: (p, size) =>
              set({
                page: size !== pageSize ? null : p === 1 ? null : p,
                pageSize: size === 10 ? null : size,
              }),
          }}
        />
      </Card>
        }
      >
        {selectedId && (
          <ListenerDetailView
            id={selectedId}
            onClose={closeListener}
            extra={
              selectedRow ? (
                <Button type="primary" onClick={() => review(selectedRow)}>
                  Review & actions
                </Button>
              ) : undefined
            }
          />
        )}
      </MasterDetail>

      {/* Detail drawer */}
      <Drawer
        title={reviewing ? listenerText({ displayName: reviewing.displayName, handle: reviewing.user.handle, id: reviewing.id }) : ''}
        open={!!reviewing && !quick}
        onClose={() => setReviewing(null)}
        width={560}
        extra={
          reviewing && (
            <Space>
              <Button
                onClick={() => {
                  setReviewing(null);
                  openListener(reviewing.id);
                }}
              >
                Activity & earnings
              </Button>
              {reviewing.status === 'pending' && (
                <>
                  <Can permission="listeners.approve">
                    <Button
                      type="primary"
                      onClick={() =>
                        setApproveModal({
                          tier: reviewing.tier,
                          // stored paise is the caller CHARGE; show the listener's
                          // video rate, stored verbatim as paise (÷100 to show ₹).
                          rateVideoRupees: Number(reviewing.perMinuteRatePaise) / 100,
                        })
                      }
                    >
                      Approve
                    </Button>
                  </Can>
                  <Can permission="listeners.schedule_interview">
                    <Button
                      onClick={() =>
                        setScheduleModal({
                          scheduledAt: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16),
                          durationMin: 15,
                          meetingLink: '',
                        })
                      }
                    >
                      Schedule interview
                    </Button>
                  </Can>
                  <Can permission="listeners.reject">
                    <Button danger onClick={() => { setRejectOpen(true); setRejectReason(''); }}>
                      Reject
                    </Button>
                  </Can>
                </>
              )}
              {reviewing.status === 'interview_scheduled' && (
                <>
                  <Can permission="listeners.approve">
                    <Button
                      type="primary"
                      onClick={() =>
                        setApproveModal({
                          tier: reviewing.tier,
                          // stored paise is the caller CHARGE; show the listener's
                          // video rate, stored verbatim as paise (÷100 to show ₹).
                          rateVideoRupees: Number(reviewing.perMinuteRatePaise) / 100,
                        })
                      }
                    >
                      Approve
                    </Button>
                  </Can>
                  <Can permission="listeners.schedule_interview">
                    <Button
                      onClick={() =>
                        setScheduleModal({
                          scheduledAt: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16),
                          durationMin: 15,
                          meetingLink: '',
                        })
                      }
                    >
                      Reschedule interview
                    </Button>
                  </Can>
                  <Can permission="listeners.reject">
                    <Button danger onClick={() => { setRejectOpen(true); setRejectReason(''); }}>
                      Reject
                    </Button>
                  </Can>
                </>
              )}
              {reviewing.status === 'approved' && (
                <Can permission="listeners.set_rate">
                  <Button
                    onClick={() =>
                      setRateModal({
                        id: reviewing.id,
                        rateVideoRupees: Number(reviewing.perMinuteRatePaise) / 100,
                      })
                    }
                  >
                    Edit rate
                  </Button>
                </Can>
              )}
              {reviewing.status === 'approved' && (
                <Button
                  type={reviewing.verifiedProfessional ? 'default' : 'primary'}
                  loading={verifyProfessional.isPending}
                  onClick={() =>
                    verifyProfessional.mutate({
                      id: reviewing.id,
                      verified: !reviewing.verifiedProfessional,
                    })
                  }
                >
                  {reviewing.verifiedProfessional ? 'Remove verification' : 'Mark verified'}
                </Button>
              )}
              {reviewing.status === 'approved' && !reviewing.isSuspended && (
                <Can permission="listeners.suspend">
                  <Button danger onClick={() => setSuspendModal({ reason: '', days: 7 })}>
                    Suspend
                  </Button>
                </Can>
              )}
              {reviewing.status === 'approved' && !reviewing.isSuspended && (
                <Can permission="listeners.dummy">
                  <Button onClick={() => setDummyModal({ enabled: !reviewing.isDummy, reason: '' })}>
                    {reviewing.isDummy ? 'Unmark dummy' : 'Mark as dummy'}
                  </Button>
                </Can>
              )}
              {reviewing.status === 'approved' && !reviewing.isSuspended && (reviewing.isHot || !reviewing.isDummy) && (
                <Can permission="listeners.hot">
                  <Button
                    loading={setHot.isPending}
                    onClick={() => setHot.mutate({ id: reviewing.id, enabled: !reviewing.isHot })}
                  >
                    {reviewing.isHot ? 'Unmark hot' : 'Mark as hot'}
                  </Button>
                </Can>
              )}
            </Space>
          )
        }
      >
        {reviewing && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={1} size="small" labelStyle={{ width: 130 }}>
              <Descriptions.Item label="Phone"><ListenerPhone user={reviewing.user} /></Descriptions.Item>
              <Descriptions.Item label="Tier"><Tag color={tierColor[reviewing.tier]}>{tierLabel[reviewing.tier]}</Tag></Descriptions.Item>
              <Descriptions.Item label="Status">
                <Space size={4} wrap>
                  <span>{reviewing.status}</span>
                  {reviewing.isSuspended && <Tag color="error">suspended</Tag>}
                  {reviewing.isDummy && <Tag color="purple">dummy</Tag>}
                  {reviewing.isHot && <Tag color="volcano">hot</Tag>}
                </Space>
              </Descriptions.Item>
              {reviewing.isHot && (
                <Descriptions.Item label="Hot since">
                  <Space direction="vertical" size={0}>
                    <span>{reviewing.hotSetAt ? new Date(reviewing.hotSetAt).toLocaleString() : '—'}</span>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Up to 5 buddies are HOT: live first, then yesterday's reliable answerers, then admin marks.
                    </Typography.Text>
                  </Space>
                </Descriptions.Item>
              )}
              {reviewing.isDummy && (
                <Descriptions.Item label="Dummy since">
                  {reviewing.dummySetAt ? new Date(reviewing.dummySetAt).toLocaleString() : '—'}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Verified counselor">
                {reviewing.verifiedProfessional
                  ? <Tag color="gold">Verified ✓</Tag>
                  : <Tag>Not verified</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Languages">{reviewing.languages.join(', ')}</Descriptions.Item>
              <Descriptions.Item label="Expertise">{reviewing.expertiseTags.join(', ')}</Descriptions.Item>
              <Descriptions.Item label="Rate (video, admin-set)">
                ₹{Number(reviewing.perMinuteRatePaise) / 100} / min
              </Descriptions.Item>
            </Descriptions>
            <div>
              <Typography.Text strong>Bio</Typography.Text>
              <Card size="small" style={{ marginTop: 6 }}>
                <Typography.Paragraph style={{ margin: 0 }}>
                  {reviewing.bio || <i>No bio provided</i>}
                </Typography.Paragraph>
              </Card>
            </div>
            <div>
              <Typography.Text strong>KYC documents</Typography.Text>
              {reviewing.kycDocuments.length === 0 ? (
                <Empty description="No documents uploaded" />
              ) : (
                <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                  {reviewing.kycDocuments.map((d) => (
                    <Card size="small" key={d.id}>
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space>
                          <Tag>{d.type.toUpperCase()}</Tag>
                          <Tag color={d.status === 'verified' ? 'success' : d.status === 'rejected' ? 'error' : 'processing'}>
                            {d.status}
                          </Tag>
                          {d.last4 && <span style={{ color: '#8B8B8B' }}>•••• {d.last4}</span>}
                        </Space>
                        <Button
                          size="small"
                          onClick={async () => {
                            const r = await api.get<{ url: string }>(`/admin/kyc/${d.id}/view-url`, {
                              params: { original: d.type === 'aadhaar' ? false : true },
                            });
                            window.open(r.data.url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          View
                        </Button>
                      </Space>
                      {d.rejectionReason && (
                        <Typography.Paragraph type="danger" style={{ marginTop: 8, marginBottom: 0 }}>
                          {d.rejectionReason}
                        </Typography.Paragraph>
                      )}
                    </Card>
                  ))}
                </Space>
              )}
            </div>
          </Space>
        )}
      </Drawer>

      {/* Approve modal */}
      <Modal
        title="Approve listener"
        open={!!approveModal && !!reviewing}
        onCancel={() => setApproveModal(null)}
        onOk={() =>
          reviewing && approveModal &&
          approve.mutate({
            id: reviewing.id,
            // Single listener tier now — always 'peer'.
            tier: 'peer',
            // Listener's reference rate (paise). Caller pricing is admin-set per
            // tier on the Call pricing page — this no longer drives the charge.
            perMinuteRatePaise: String(Math.round(approveModal.rateVideoRupees * 100)),
          })
        }
        okText="Approve"
        okButtonProps={{ loading: approve.isPending }}
      >
        {approveModal && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Typography.Text>Tier</Typography.Text>
              <Select<Tier>
                style={{ width: '100%', marginTop: 4 }}
                value="peer"
                disabled
                options={[{ value: 'peer', label: 'Listener' }]}
              />
            </div>
            <div>
              <Typography.Text>Per-minute VIDEO rate (₹) — audio bills ⅓</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={1}
                max={100}
                value={approveModal.rateVideoRupees}
                onChange={(rateVideoRupees) =>
                  rateVideoRupees !== null && setApproveModal({ ...approveModal, rateVideoRupees })
                }
              />
            </div>
          </Space>
        )}
      </Modal>

      {/* Edit-rate modal (admin sets the per-minute call rate) */}
      <Modal
        title="Edit per-minute rate"
        open={!!rateModal}
        onCancel={() => setRateModal(null)}
        okText="Save rate"
        confirmLoading={setRate.isPending}
        okButtonProps={{ disabled: !rateModal || rateModal.rateVideoRupees == null }}
        onOk={() =>
          rateModal &&
          setRate.mutate({
            id: rateModal.id,
            perMinuteRatePaise: String(Math.round(rateModal.rateVideoRupees * 100)),
          })
        }
      >
        {rateModal && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              Per-minute VIDEO rate. Audio calls bill at ⅓ of this. This is what
              the caller pays; the listener earns their revenue share on top.
            </Typography.Text>
            <InputNumber
              min={0}
              max={1000}
              step={0.5}
              addonBefore="₹"
              addonAfter="/ min"
              style={{ width: '100%' }}
              value={rateModal.rateVideoRupees}
              onChange={(rateVideoRupees) =>
                rateVideoRupees !== null && setRateModal({ ...rateModal, rateVideoRupees })
              }
            />
          </Space>
        )}
      </Modal>

      {/* Reject modal */}
      <Modal
        title="Reject listener"
        open={rejectOpen}
        onCancel={() => { setRejectOpen(false); setRejectReason(''); }}
        onOk={() => reviewing && reject.mutate({ id: reviewing.id, reason: rejectReason })}
        okText="Reject"
        okButtonProps={{
          danger: true,
          disabled: rejectReason.trim().length < 10,
          loading: reject.isPending,
        }}
      >
        <Typography.Paragraph type="secondary">
          The listener will see this reason. Be specific. ≥ 10 characters.
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      {/* Schedule-interview modal */}
      <Modal
        title={reviewing?.status === 'interview_scheduled' ? 'Reschedule interview' : 'Schedule interview'}
        open={!!scheduleModal && !!reviewing}
        onCancel={() => setScheduleModal(null)}
        onOk={() =>
          reviewing &&
          scheduleModal &&
          scheduleInterview.mutate({
            id: reviewing.id,
            scheduledAt: new Date(scheduleModal.scheduledAt).toISOString(),
            durationMin: scheduleModal.durationMin,
            meetingLink: scheduleModal.meetingLink,
          })
        }
        okText={reviewing?.status === 'interview_scheduled' ? 'Reschedule' : 'Schedule'}
        okButtonProps={{
          loading: scheduleInterview.isPending,
          disabled: !!scheduleModal && meetingLinkError(scheduleModal.meetingLink) !== null,
        }}
      >
        {scheduleModal && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Typography.Text>When (local)</Typography.Text>
              <Input
                type="datetime-local"
                style={{ marginTop: 4 }}
                value={scheduleModal.scheduledAt}
                onChange={(e) => setScheduleModal({ ...scheduleModal, scheduledAt: e.target.value })}
              />
            </div>
            <div>
              <Typography.Text>Duration (minutes)</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={10}
                max={60}
                value={scheduleModal.durationMin}
                onChange={(v) => v !== null && setScheduleModal({ ...scheduleModal, durationMin: v })}
              />
            </div>
            <div>
              <Typography.Text>Meeting link (Google Meet / Zoom)</Typography.Text>
              <Input
                style={{ marginTop: 4 }}
                placeholder="https://meet.google.com/abc-defg-hij"
                value={scheduleModal.meetingLink}
                status={meetingLinkError(scheduleModal.meetingLink) ? 'error' : undefined}
                onChange={(e) =>
                  setScheduleModal({ ...scheduleModal, meetingLink: e.target.value })
                }
              />
              {meetingLinkError(scheduleModal.meetingLink) && (
                <Typography.Text type="danger" style={{ fontSize: 11, display: 'block' }}>
                  {meetingLinkError(scheduleModal.meetingLink)}
                </Typography.Text>
              )}
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                Shown to the listener in the app as soon as it's saved; the Join
                button enables ~10 min before. Leave blank to add it later.
              </Typography.Text>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              The listener gets a push + email with the time. They appear in the
              <code> interview_scheduled</code> filter until you approve or reject.
            </Typography.Text>
          </Space>
        )}
      </Modal>

      {/* Suspend modal */}
      <Modal
        title="Suspend listener"
        open={!!suspendModal && !!reviewing}
        onCancel={() => setSuspendModal(null)}
        onOk={() =>
          reviewing && suspendModal &&
          suspend.mutate({ id: reviewing.id, reason: suspendModal.reason, durationDays: suspendModal.days })
        }
        okText="Suspend"
        okButtonProps={{
          danger: true,
          disabled: !suspendModal || suspendModal.reason.trim().length < 10,
          loading: suspend.isPending,
        }}
      >
        {suspendModal && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Typography.Text>Duration (days)</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={1}
                max={365}
                value={suspendModal.days}
                onChange={(days) => days !== null && setSuspendModal({ ...suspendModal, days })}
              />
            </div>
            <div>
              <Typography.Text>Reason</Typography.Text>
              <Input.TextArea
                rows={3}
                value={suspendModal.reason}
                onChange={(e) => setSuspendModal({ ...suspendModal, reason: e.target.value })}
              />
            </div>
          </Space>
        )}
      </Modal>

      {/* Dummy listener modal */}
      <Modal
        title={dummyModal?.enabled ? 'Mark as dummy listener' : 'Unmark dummy listener'}
        open={!!dummyModal && !!reviewing}
        onCancel={() => setDummyModal(null)}
        onOk={() =>
          reviewing && dummyModal &&
          setDummy.mutate({ id: reviewing.id, enabled: dummyModal.enabled, reason: dummyModal.reason.trim() })
        }
        okText={dummyModal?.enabled ? 'Mark as dummy' : 'Unmark dummy'}
        okButtonProps={{
          disabled: !dummyModal || dummyModal.reason.trim().length < 10,
          loading: setDummy.isPending,
        }}
      >
        {dummyModal && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Shown as online to users. Never rings. A call to this profile moves to the next real
              listener after the dummy attempt timeout (Settings → Calls).
            </Typography.Text>
            <div>
              <Typography.Text>Reason</Typography.Text>
              <Input.TextArea
                rows={3}
                value={dummyModal.reason}
                placeholder="Reason (≥ 10 chars) — captured in the audit log."
                onChange={(e) => setDummyModal({ ...dummyModal, reason: e.target.value })}
              />
            </div>
          </Space>
        )}
      </Modal>
    </Space>
  );
}
